import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { injectIntl, useIntl } from 'react-intl'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import format from "date-fns/format";
import frLocale from "date-fns/locale/fr";
import ruLocale from "date-fns/locale/ru";
import enLocale from "date-fns/locale/en-US";
import enGBLocale from "date-fns/locale/en-GB";
import esLocale from "date-fns/locale/es";
import itLocale from "date-fns/locale/it";
import deLocale from "date-fns/locale/de";
import daLocale from "date-fns/locale/da";
import plLocale from "date-fns/locale/pl";
import trLocale from "date-fns/locale/tr";
import nlLocale from "date-fns/locale/nl";
import { useTheme } from '@material-ui/core/styles'




const useInputStyles = makeStyles((theme) => ({
	root: {
		color: 'black',
		position: 'relative',
		fontSize: '16px',
		transition: 'background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
		'border-top-left-radius': '4px',
		'border-top-right-radius': '4px',
		marginTop: '0',
		marginBottom: '0',
		minWidth: '182px',
		minHeight: '57px',
		[theme.breakpoints.down('xs')]: {
			minWidth: 'auto',
		},
		'&$disabled': {
			color: '#6C737B',
			backgroundColor: "rgba(0, 0, 0, 0.12)",
			opacity: "0.4"
		},
		'& .MuiInputAdornment-positionEnd': {
			marginTop: '4px',
			[theme.breakpoints.down('xs')]: {
				marginLeft: '0',
				marginRight: '0',
			},
		},
		'& .MuiIconButton-root': {
			[theme.breakpoints.down('xs')]: {
				padding: '12px 0',
			},
		}
	},
	focused: {
		color: 'black',
		backgroundColor: 'rgb(0,0,0,.12)',
		'&$focused': {
			color: 'black',
			backgroundColor: 'rgb(0,0,0,.12))',
		},
	},
	disabled: {
		color: '#6C737B',
	}
}));

const useLabelStyles = makeStyles((theme) => ({
	root: {
		color: 'black',
		backgroundColor: 'rgb(0,0,0,.12))'
	},
	focused: {
		color: 'black',
		backgroundColor: 'rgb(0,0,0,.12))',
		'&$focused': {
			color: 'black',
			backgroundColor: 'rgb(0,0,0,.12))',
		},
	},
	disabled: {
		color: '#6C737B',
		marginLeft: '0'
	}
}));

const useHelperTextStyles = makeStyles((theme) => ({
	root: {
		marginLeft: 0,
		marginRight: 0,
		backgroundColor: 'white'
	}
}));

const localeMap = {
	en: enLocale,
	es: esLocale,
	fr: frLocale,
	ru: ruLocale,
	it: itLocale,
	de: deLocale,
	da: daLocale,
	pl: plLocale,
	tr: trLocale,
	'en-GB': enGBLocale,
	nl: nlLocale
}

class LocalizedUtils extends DateFnsUtils {
	getCalendarHeaderText(date) {
		return format(date, "LLLL", { locale: this.locale });
	}

	getDatePickerHeaderText(date) {
		return format(date, useIntl().formatMessage({ id: 'app.general.datePickerHeaderFormat' }), { locale: this.locale });
	}
}

const transformDate = (date) => {
	if(date){
		return date.toLocaleDateString('en-US')
	}

	return date
}

const LocalizedDatePicker = ({
	defaultDate,
	onDateChange,
	label,
	disabled,
	error,
	externalInputStyles,
	externalLabelStyles,
	externalHelperStyles,
	inputProps,
	openToYear,
	intl,
	touched,
	disablePast
}) => {
	const [changed, setChanged] = useState(false)
	const [currentDate, setCurrentDate] = useState(defaultDate ? transformDate(new Date(defaultDate)) : null)
	const [errorText, setErrorText] = useState(null)
	const [helperText, setHelperText] = useState(null)

	useEffect(() => {
		setTimeout(() => setHelperText(changed && checkError()), 200)
	}, [changed, errorText])

	useEffect(() => {
		setChanged(touched)
	}, [touched])

	useEffect(() => {
		setCurrentDate(defaultDate ? transformDate(new Date(defaultDate)) : null)
	}, [defaultDate])

	useEffect(
		() => {
			setErrorText(error)
		},
		[error]
	)

	var inputStyles = externalInputStyles ?? useInputStyles()
	var labelStyles = externalLabelStyles ?? useLabelStyles()
	var helperStyles = externalHelperStyles ?? useHelperTextStyles()

	const handleDateChange = (date) => {
		setChanged(false)
		const newDate = transformDate(date)
		setCurrentDate(newDate)
		if (onDateChange) {
			onDateChange(newDate)
		}
	}

	const checkError = () => {
		return errorText === 'Required' && currentDate ? null : errorText
	}

	return (
		<MuiPickersUtilsProvider utils={LocalizedUtils} locale={localeMap[intl.locale] || enLocale}>
			<KeyboardDatePicker
				data-testid="survey-change-date-modal-date-picker"
				autoOk
				variant='inline'
				format={intl.formatMessage({ id: 'app.general.datePickerFormat' })}
				margin='normal'
				id='date-picker-inline'
				label={label}
				value={currentDate}
				disabled={disabled}
				placeholder={intl.formatMessage({ id: 'app.general.datePickerPlaceholder' })}
				onChange={handleDateChange}
				inputVariant='filled'
				helperText={helperText}
				InputProps={{
					classes: {
						root: inputStyles.root,
						disabled: inputStyles.disabled
					}
				}}
				InputLabelProps={{
					classes: {
						root: labelStyles.root,
						focused: labelStyles.focused,
						disabled: labelStyles.disabled
					}
				}}
				FormHelperTextProps={{
					classes: {
						root: helperStyles.root,
						contained: helperStyles.root
					}
				}}
				classes={{
					root: inputStyles.root,
					disabled: inputStyles.disabled
				}}
				inputProps={inputProps}
				error={changed && checkError()}
				onBlur={() => setChanged(true)}
				onAccept={() => setChanged(true)}
				views={['year', 'month', 'date']}
				openTo={openToYear ? 'year' : 'date'}
				KeyboardButtonProps={inputProps}
				disablePast={disablePast}
			/>
		</MuiPickersUtilsProvider>
	)
}

const mapStateToProps = () => ({})

const mapDispatchToProps = () => ({})

const enhance = compose(connect(mapStateToProps, mapDispatchToProps), injectIntl)

export default enhance(LocalizedDatePicker)
